import React from "react";
import {
  FaTimes,
  FaSave,
  FaSpinner,
  FaLayerGroup,
  FaFileAlt,
  FaImage,
  FaMusic,
} from "react-icons/fa";

interface ExamPart {
  id: number;
  displayName: string;
  contextText: string | null;
  hasContextImage: boolean;
  hasContextDocument: boolean;
  hasContextAudio: boolean;
}

interface Props {
  isOpen: boolean;
  part: ExamPart | null;
  contextText: string;
  imageFile: File | null;
  documentFile: File | null;
  audioFile: File | null;
  uploading: boolean;
  onClose: () => void;
  onSave: () => void;
  onTextChange: (text: string) => void;
  onImageChange: (file: File | null) => void;
  onDocumentChange: (file: File | null) => void;
  onAudioChange: (file: File | null) => void;
}

export const ContextModal: React.FC<Props> = ({
  isOpen,
  part,
  contextText,
  imageFile,
  documentFile,
  audioFile,
  uploading,
  onClose,
  onSave,
  onTextChange,
  onImageChange,
  onDocumentChange,
  onAudioChange,
}) => {
  if (!isOpen || !part) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <FaLayerGroup />
            Manage Context: {part.displayName}
          </h3>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <FaTimes size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Shared Context</strong> - Shown to students before
              questions
            </p>
          </div>

          {/* Text */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaFileAlt className="text-indigo-600" />
              Context Text
            </label>
            <textarea
              value={contextText}
              onChange={(e) => onTextChange(e.target.value)}
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter shared text (e.g., article, email)"
            />
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaImage className="text-indigo-600" />
              Context Image
            </label>
            {part.hasContextImage && !imageFile && (
              <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  ✅ Current image uploaded
                </p>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => onImageChange(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            {imageFile && (
              <p className="text-sm text-green-600 mt-2">
                ✅ New: {imageFile.name}
              </p>
            )}
          </div>

          {/* PDF */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaFileAlt className="text-indigo-600" />
              Context Document (PDF)
            </label>
            {part.hasContextDocument && !documentFile && (
              <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  ✅ Current document uploaded
                </p>
              </div>
            )}
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => onDocumentChange(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            {documentFile && (
              <p className="text-sm text-green-600 mt-2">
                ✅ New: {documentFile.name}
              </p>
            )}
          </div>

          {/* Audio */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaMusic className="text-indigo-600" />
              Context Audio (Teil-specific)
            </label>
            {part.hasContextAudio && !audioFile && (
              <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  ✅ Current audio uploaded
                </p>
              </div>
            )}
            <input
              type="file"
              accept="audio/*,video/mp4"
              onChange={(e) => onAudioChange(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            {audioFile && (
              <p className="text-sm text-green-600 mt-2">
                ✅ New: {audioFile.name}
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onSave}
              disabled={uploading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Context
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
